import { BadgeProps } from '@zoopla-eng/zds/badges';
import { uniqBy } from 'lodash';

type BadgeLabels =
    | 'Business for sale'
    | 'Equity loan'
    | 'Expired'
    | 'Investment'
    | 'Just added'
    | 'Let agreed'
    | 'Let'
    | 'New build'
    | 'New home'
    | 'Part buy part rent'
    | 'Shared equity'
    | 'Shared ownership'
    | 'Sold subject to contract'
    | 'Student friendly'
    | 'Under offer';

export function getBadgePropsForText(text: string): BadgeProps {
    const tagLabelToBadgeProps: {
        [key in BadgeLabels | string]: BadgeProps;
    } = {
        Premium: {
            text: 'Premium',
            level: 1
        },
        Expired: {
            level: 2,
            text: 'Expired',
            bg: 'functionalFailure',
            color: 'textContrast'
        },
        Featured: { text: 'Featured', level: 2 },
        'Just added': {
            text: 'Just added',
            level: 2
        },
        'Under offer': {
            text: 'Under offer',
            level: 3
        },
        'Sold subject to contract': {
            text: 'Sold STC',
            level: 3
        },
        'Let agreed': {
            text: 'Let agreed',
            level: 3
        },
        Let: {
            text: 'Let',
            level: 3
        },
        'Equity loan': {
            text: 'Equity loan',
            level: 4
        },
        'Part buy part rent': {
            text: 'Part buy part rent',
            level: 4
        },
        'Shared equity': {
            text: 'Shared equity',
            level: 4
        },
        'Shared ownership': {
            text: 'Shared ownership',
            level: 4
        },
        'Student friendly': {
            text: 'Student friendly',
            level: 4
        },
        Investment: {
            text: 'Investment',
            level: 4
        },
        'New build': {
            text: 'New build',
            level: 4
        },
        'New home': {
            // We're always calling it 'New build'
            text: 'New build',
            level: 4
        },
        'Business for sale': {
            text: 'Business',
            level: 4
        }
    };

    return tagLabelToBadgeProps[text] || { text, level: 4 };
}

export function getBadgesForListing({
    statusSummaryLabel = '',
    tagLabels = []
}: {
    statusSummaryLabel?: string | null | undefined;
    tagLabels?: Array<string>;
}): Array<BadgeProps> {
    const relevantBadges = [];

    if (statusSummaryLabel) {
        relevantBadges.push(statusSummaryLabel);
    }

    tagLabels.forEach((label) => {
        if (label) {
            relevantBadges.push(label);
        }
    });

    if (!relevantBadges.length) return [];

    const badgeProps = relevantBadges.map(getBadgePropsForText);

    return uniqBy(badgeProps, 'text');
}

export function getBadgeForSavedListing({
    isExpired,
    isNewBuild,
    statusSummaryLabel,
    tagLabels
}: {
    isNewBuild?: boolean;
    isExpired?: boolean;
    statusSummaryLabel?: string | null | undefined;
    tagLabels?: Array<string> | null | undefined;
}): BadgeProps | null {
    if (isExpired) return getBadgePropsForText('Expired');

    const isBusiness = !!tagLabels?.includes('Business for sale');
    const isJustAdded = statusSummaryLabel === 'Just added';

    if (isJustAdded && isNewBuild) return getBadgePropsForText('New build');
    if (isJustAdded && isBusiness) {
        return getBadgePropsForText('Business for sale');
    }
    if (statusSummaryLabel) return getBadgePropsForText(statusSummaryLabel);
    if (isNewBuild) return getBadgePropsForText('New build');
    if (isBusiness) return getBadgePropsForText('Business for sale');

    return null;
}
